import React, { useEffect, useRef } from 'react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';

// MathJax 설정
const config = {
  loader: { load: ["input/tex", "output/svg"] },
  tex: {
    inlineMath: [["$", "$"], ["\\(", "\\)"]],
    displayMath: [["$$", "$$"], ["\\[", "\\]"]],
    processEscapes: true,
    processEnvironments: true
  },
  svg: {
    fontCache: 'global'
  }
};

/**
 * 수학 표현식 렌더링 컴포넌트
 * 
 * @param {Object} props 컴포넌트 프롭스
 * @param {string} props.content - LaTeX 수식이 포함된 텍스트 내용
 * @param {boolean} props.inline - 인라인 수식 여부 (기본값: false)
 * @param {Object} props.mathJaxOptions - MathJax에 전달할 추가 옵션
 */
const MathRenderer = ({ content, inline = false, mathJaxOptions = {} }) => {
  // 컴포넌트 참조 생성
  const mathRef = useRef(null);
  
  // 컨텐츠가 없으면 렌더링하지 않음
  if (!content) return null;

  // 수학 표현식 정제 함수
  const sanitizeLatex = (text) => {
    if (!text) return text;
    
    let result = text;
    
    // 1. 공백 문자 정리 (탭, 여러 공백 등)
    result = result.replace(/\t/g, ' ').replace(/\s+/g, ' ').trim();
    
    // 2. 명령어 앞의 백슬래시 정규화 (중복 백슬래시 제거, 누락된 백슬래시 추가)
    const commands = [
      'frac', 'sqrt', 'text', 'times', 'log', 'sin', 'cos', 'tan', 
      'lim', 'int', 'sum', 'prod', 'alpha', 'beta', 'gamma', 'delta',
      'pi', 'theta', 'infty', 'approx', 'neq', 'geq', 'leq', 'pm', 'div',
      'cdot', 'ldots', 'partial', 'nabla', 'forall', 'exists', 'in', 'binom'
    ];
    
    // 2.1. 다중 백슬래시 정규화 (\\\\frac -> \\frac)
    result = result.replace(/\\{2,}([a-zA-Z]+)/g, '\\$1');
    
    // 2.2. 잘못된 \f\frac 패턴 수정
    result = result.replace(/\\f\\frac/g, '\\frac');
    result = result.replace(/\\f\\?frac/g, '\\frac');
    
    // 2.3. \f\f^숫자_숫자 형식을 \frac{숫자}{숫자}로 변환
    result = result.replace(/\\f\\f\^([0-9]+)_([0-9]+)/g, '\\frac{$1}{$2}');
    result = result.replace(/\\f\^([0-9]+)_([0-9]+)/g, '\\frac{$1}{$2}');
    
    // 2.3.1 추가 LaTeX 형식 패턴 처리
    // \f^숫자_숫자 패턴 처리
    result = result.replace(/\\f\s*\^([0-9]+)_([0-9]+)/g, '\\frac{$1}{$2}');
    // 중간에 공백이 있는 경우 처리 (예: \f \f^3_4)
    result = result.replace(/\\f\s*\\f\s*\^([0-9]+)_([0-9]+)/g, '\\frac{$1}{$2}');
    // x^숫자_숫자 패턴 처리
    result = result.replace(/([a-zA-Z])\^([0-9]+)_([0-9]+)/g, '$1^{\\frac{$2}{$3}}');
    
    // 2.4. 누락된 백슬래시 추가
    commands.forEach(cmd => {
      // 백슬래시 없이 명령어로 시작하거나 공백 뒤에 명령어가 오는 경우
      const regex = new RegExp(`(^|[^\\\\])\\b${cmd}\\b`, 'g');
      result = result.replace(regex, `$1\\${cmd}`);
      
      // \뒤에 공백이 있는 경우 (\ frac -> \frac)
      result = result.replace(new RegExp(`\\\\\\s+${cmd}`, 'g'), `\\${cmd}`);
    });
    
    // 3. 특정 명령어/기호 오류 수정
    // 3.1. \times 기호 처리 (곱하기 기호)
    // 명시적으로 23\times45 패턴을 처리
    result = result.replace(/(\d+)\\t?times(\d+)/g, '$1\\times$2');
    // 다른 times 패턴 처리
    result = result.replace(/\\\\\s*times/g, '\\times'); // \\times -> \times
    result = result.replace(/\\t\s*times/g, '\\times'); // \ttimes -> \times
    result = result.replace(/\\ttimes/g, '\\times'); // \ttimes -> \times
    result = result.replace(/([^\\]|^)times/g, '$1\\times'); // times -> \times
    
    // 3.1.1 \t\times 또는 \t times 패턴 처리
    result = result.replace(/\\t\\times/g, '\\times');
    result = result.replace(/\\t\s+times/g, '\\times');
    result = result.replace(/([^t])imes/g, '$1\\times');
    result = result.replace(/([^\\\s])times/g, '$1\\times');
    result = result.replace(/\bimes\b/g, '\\times');
    
    // 3.2. ext -> text (텍스트 명령어)
    result = result.replace(/\\ext\{/g, '\\text{');
    result = result.replace(/ext\{/g, 'text{');
    
    // 3.2.1 tttext -> text 변환 (텍스트 명령어의 다른 형태)
    result = result.replace(/\\tttext/g, '\\text');
    result = result.replace(/tttext/g, '\\text');
    
    // 3.3. 로그 함수 표기법 수정
    result = result.replace(/\\text\{log\}_/g, '\\log_');
    result = result.replace(/log_/g, '\\log_');
    
    // 3.4. 수식 내 괄호와 중괄호 균형 확인 및 수정
    let openBraces = (result.match(/\{/g) || []).length;
    let closeBraces = (result.match(/\}/g) || []).length;
    
    // 중괄호 {} 균형 맞추기
    while (openBraces > closeBraces) {
      result += '}';
      closeBraces++;
    }
    while (closeBraces > openBraces) {
      result = result.replace(/\}([^}]*)$/, '$1');
      closeBraces--;
    }
    
    // 4. 잘못된 공백 처리
    // 4.1. 명령어와 괄호 사이 공백 제거
    result = result.replace(/\\([a-zA-Z]+)\s+\{/g, '\\$1{');
    
    // 4.2. 구분자 앞뒤 공백 정규화
    result = result.replace(/\s*\}\s*\{/g, '}{');
    
    // 5. 논리적 연산자 및 기호 처리
    // \equals는 실제로 존재하지 않는 명령어이므로 = 기호 그대로 사용
    result = result.replace(/\\equals/g, '=');
    // = 기호 앞뒤에 공백 추가
    result = result.replace(/([^\s=])=([^\s=])/g, '$1 = $2');
    
    // 숫자와 분수 사이의 오류 처리
    result = result.replace(/(\d)\s*\\frac/g, '$1 \\cdot \\frac');
    
    return result;
  };

  // LaTeX 수식 정제
  const processedContent = sanitizeLatex(content);
  
  // 컨텐츠 내에 이미 $ 기호가 있는지 확인
  const hasInlineMath = processedContent.includes('$');
  const hasDisplayMath = processedContent.includes('$$');
  
  // 최종 처리된 컨텐츠
  let finalContent = processedContent;
  
  // LaTeX 명령어가 있지만 $ 기호가 없는 경우 추가
  if (!hasInlineMath && !hasDisplayMath) {
    const latexCommands = ['\\frac', '\\sqrt', '\\log', '\\sin', '\\cos', 
                         '\\lim', '\\int', '\\sum', '\\times', '\\text', '\\binom'];
    const containsLatex = latexCommands.some(cmd => processedContent.includes(cmd));
    
    if (containsLatex) {
      // LaTeX 명령어가 포함되어 있으면 $ 기호로 묶어줌
      finalContent = `$${processedContent}$`;
    }
  }
  
  // 이미 $가 있는 경우, 수식 내부의 오류를 확인하고 수정
  if (hasInlineMath) {
    // $ 기호 사이의 내용만 추출하여 정제
    finalContent = finalContent.replace(/\$(.*?)\$/g, (match, p1) => {
      const cleanedMath = sanitizeLatex(p1);
      return `$${cleanedMath}$`;
    });
  }

  try {
    return (
      <MathJax inline={inline} {...mathJaxOptions} style={{ display: 'inline-block' }} ref={mathRef}>
        {finalContent}
      </MathJax>
    );
  } catch (error) {
    // 오류 발생 시 일반 텍스트로 표시
    return (
      <div>
        <span style={{ color: '#d00' }}>Math input error: </span>
        <span>{content}</span>
      </div>
    );
  }
};

/**
 * 애플리케이션 전역에서 사용할 MathJax 컨텍스트 제공자
 */
export const MathJaxProvider = ({ children }) => {
  const isPrerender = (
    typeof navigator !== 'undefined' &&
    /HeadlessChrome|ReactSnap|Prerender/i.test(navigator.userAgent || '')
  );

  // MathJax 전역 렌더링 함수 추가
  useEffect(() => {
    if (isPrerender) return undefined;

    // MathJax 재렌더링 함수 정의
    const reprocessMathJax = () => {
      if (window.MathJax && window.MathJax.typeset) {
        try {
          window.MathJax.typeset();
        } catch (error) {
          console.error('수식 전역 재렌더링 중 오류 발생:', error);
        }
      }
    };

    // 윈도우 리사이즈 이벤트에 연결
    window.addEventListener('resize', reprocessMathJax);
    
    // 최초 렌더링 시도
    const initialRenderTimer = setTimeout(() => {
      reprocessMathJax();
    }, 500); // 초기 로딩 후 500ms 지연
    
    // 클린업 함수
    return () => {
      window.removeEventListener('resize', reprocessMathJax);
      clearTimeout(initialRenderTimer);
    };
  }, [isPrerender]);

  if (isPrerender) {
    return <>{children}</>;
  }

  return (
    <MathJaxContext config={config}>
      {children}
    </MathJaxContext>
  );
};

export default MathRenderer; 